import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { MapPin, HardHat, ChevronRight, Phone, Mail, Globe } from 'lucide-react';
import PoweredByBadge from '../components/PoweredByBadge';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE = API_URL.replace('/api', '');

const STATUS_LABEL = {
  NOT_STARTED: 'Non démarré',
  IN_PROGRESS: 'En cours',
  ON_HOLD: 'En pause',
  COMPLETED: 'Terminé',
};
const STATUS_COLORS = {
  NOT_STARTED: { bg: '#f1f5f9', text: '#475569' },
  IN_PROGRESS: { bg: '#dbeafe', text: '#1d4ed8' },
  ON_HOLD:     { bg: '#fef3c7', text: '#92400e' },
  COMPLETED:   { bg: '#dcfce7', text: '#166534' },
};

function ChantierCard({ p, brand, companySlug }) {
  const sc = STATUS_COLORS[p.status] || STATUS_COLORS.IN_PROGRESS;
  return (
    <Link to={`/e/${companySlug}/chantier/${p.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div
        style={{
          background: 'white', borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
          border: '1px solid rgba(0,0,0,0.06)',
          transition: 'transform 0.15s, box-shadow 0.15s', cursor: 'pointer',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.12)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.07)'; }}
      >
        {p.coverPhoto ? (
          <img src={`${API_BASE}/uploads/${p.coverPhoto}`} alt={p.name}
            style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{
            width: '100%', height: 160, background: brand,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HardHat size={40} color="rgba(255,255,255,0.55)" />
          </div>
        )}
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>{p.name}</h3>
            <span style={{ flexShrink: 0, fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: sc.bg, color: sc.text }}>
              {STATUS_LABEL[p.status] || p.status}
            </span>
          </div>
          {p.address && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', fontSize: '0.82rem', marginBottom: 4 }}>
              <MapPin size={12} /><span>{p.address}</span>
            </div>
          )}
          {p.description && (
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.85rem', lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {p.description}
            </p>
          )}
          {p.progress !== null && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#94a3b8', marginBottom: 4 }}>
                <span>Avancement</span>
                <span style={{ color: brand, fontWeight: 600 }}>{p.progress}%</span>
              </div>
              <div style={{ height: 5, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${p.progress}%`, background: brand, borderRadius: 99 }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 12, color: brand, fontSize: '0.82rem', fontWeight: 600, gap: 4 }}>
            Voir le suivi <ChevronRight size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
}

// Page vitrine publique d'un tenant : /e/{companySlug}
// Entièrement pilotée par le branding de l'entreprise (logo, couleurs, tagline...).
export default function CompanyPublicPage() {
  const { companySlug } = useParams();
  const [searchParams] = useSearchParams();
  // Voir PublicProject : le ?source=qr arrive sur le navigateur, pas sur l'API.
  const sourceQuery = searchParams.get('source') === 'qr' ? '?source=qr' : '';
  const [company, setCompany] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      fetch(`${API_URL}/public/e/${companySlug}${sourceQuery}`).then(r => (r.ok ? r.json() : Promise.reject(r.status))),
      fetch(`${API_URL}/public/e/${companySlug}/chantiers`).then(r => (r.ok ? r.json() : [])),
    ])
      .then(([c, list]) => { setCompany(c); setChantiers(list); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [companySlug, sourceQuery]);

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Chargement...</div>;
  }
  if (notFound || !company) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
        <HardHat size={40} color="#cbd5e1" style={{ marginBottom: 12 }} />
        <p>Cette page d'entreprise n'existe pas.</p>
      </div>
    );
  }

  const brand = company.color || '#2563eb';
  const pageBg = company.pageBgColor || '#f8fafc';
  const heroBg = company.heroBgColor || pageBg;
  const logoHeight = { small: 64, medium: 96, large: 128 }[company.logoSize] || 96;

  return (
    <div style={{ minHeight: '100vh', background: pageBg, display: 'flex', flexDirection: 'column' }}>

      {/* En-tête entreprise */}
      <header style={{ background: heroBg, borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '36px 16px 28px', textAlign: company.titleAlign || 'center' }}>
        {company.logo && (
          <img src={`${API_BASE}/uploads/${company.logo}`} alt={company.name}
            style={{ height: logoHeight, width: 'auto', marginBottom: 16 }} />
        )}
        <h1 style={{
          margin: '0 0 6px',
          fontSize: company.titleSize === 'small' ? '1.3rem' : company.titleSize === 'medium' ? '1.7rem' : '2.1rem',
          color: heroBg === pageBg ? '#1e293b' : '#1e293b',
        }}>
          {company.name}
        </h1>
        {company.tagline && (
          <p style={{ fontSize: '0.9rem', fontWeight: 600, letterSpacing: '2px', color: brand, textTransform: 'uppercase', margin: '0 0 4px' }}>
            {company.tagline}
          </p>
        )}
        {company.qualibatLogo && (
          <div style={{ marginTop: 10 }}>
            <img src={`${API_BASE}/uploads/${company.qualibatLogo}`} alt="Certification" style={{ height: 72, objectFit: 'contain' }} />
          </div>
        )}
      </header>

      <main style={{ flex: 1 }}>
        {company.description && (
          <section style={{ maxWidth: 760, margin: '0 auto', padding: '36px 24px 0' }}>
            <p style={{ color: '#475569', lineHeight: 1.8, fontSize: '0.95rem', whiteSpace: 'pre-line', margin: 0 }}>
              {company.description}
            </p>
          </section>
        )}

        {/* Chantiers publics */}
        <section style={{ maxWidth: 1000, margin: '0 auto', padding: '36px 16px 48px' }}>
          <h2 style={{ fontSize: '1.15rem', color: '#1e293b', margin: '0 0 18px' }}>Nos chantiers</h2>
          {chantiers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
              <HardHat size={40} color="#cbd5e1" style={{ marginBottom: 12 }} />
              <p>Aucun chantier disponible pour le moment.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
              {chantiers.map(p => <ChantierCard key={p.slug} p={p} brand={brand} companySlug={companySlug} />)}
            </div>
          )}
        </section>

        {/* Contact */}
        {(company.phone || company.email || company.address || company.website) && (
          <section style={{ maxWidth: 600, margin: '0 auto', padding: '0 24px 48px', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.05rem', color: '#1e293b', marginBottom: 12 }}>Contact</h2>
            <div style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 2 }}>
              {company.address && <div><MapPin size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{company.address}</div>}
              {company.phone && (
                <div><Phone size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                  <a href={`tel:${company.phone.replace(/\s/g, '')}`} style={{ color: '#334155', textDecoration: 'none' }}>{company.phone}</a>
                </div>
              )}
              {company.email && (
                <div><Mail size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                  <a href={`mailto:${company.email}`} style={{ color: brand }}>{company.email}</a>
                </div>
              )}
              {company.website && (
                <div><Globe size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                  <a href={company.website} target="_blank" rel="noreferrer" style={{ color: brand }}>{company.website}</a>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <footer style={{ background: brand, color: '#fff', padding: '16px', textAlign: 'center' }}>
        <p style={{ fontSize: '0.8rem', opacity: 0.85, margin: 0, letterSpacing: '0.5px' }}>
          © {new Date().getFullYear()} {company.name} — Tous droits réservés
        </p>
      </footer>
      <PoweredByBadge show={company.poweredBy} />
    </div>
  );
}
